import {
  Comment,
  Discussion,
  Event,
  Identifiable,
  List,
  Project,
  resource,
  Resource,
  ResourceTypes,
  Task,
} from "@planzone/dto";
import { HttpClient, HttpMethods } from "@planzone/services";
import {
  LinkedPlanzoneResource,
  LinkedResource,
  Linker,
} from "../linkers/types";
import { v4 as uuid } from "uuid";
import i18next from "i18next";

type CommentsPage = {
  _embedded?: {
    comments: LinkedResource<Identifiable<Comment>>[];
  };
};

function resolveCommentsPage(
  httpClient: HttpClient,
  linker: Linker,
  { _embedded }: CommentsPage,
): Promise<Identifiable<Comment>[]> {
  if (!_embedded) return Promise.resolve([]);

  return Promise.all(
    _embedded.comments.map((comment) =>
      httpClient
        .sendHttpRequest<LinkedPlanzoneResource>({
          endpoint: comment._links.author.href,
          method: HttpMethods.GET,
          baseUrl: "",
        })
        .then((author) => linker.linkResource(author))
        .catch(() => ({
          ...resource,
          uuid: uuid(),
          name: i18next.t("common.removedCommentAuthor"),
          avatarUrl: "/public/removedAuthor.png",
          type: ResourceTypes.HUMAN,
        }))
        .then((author) => ({ ...comment, author }))
        .then((comment) =>
          httpClient
            .sendHttpRequest<{
              _embedded: {
                resources: Identifiable<Resource>[];
              };
            }>({
              endpoint: comment._links.recipients.href,
              method: HttpMethods.GET,
              baseUrl: "",
            })
            .then(({ _embedded: { resources } }) => ({
              ...comment,
              recipients: resources,
            }))
            .catch(() => comment),
        ),
    ),
  );
}

function getTaskDiscussion(httpClient: HttpClient, linker: Linker) {
  return (subject: string, page: number, size: number) =>
    httpClient
      .sendHttpRequest<LinkedResource<Task>>({
        endpoint: `/tasks/${subject}`,
        method: HttpMethods.GET,
      })
      .then((task) =>
        httpClient
          .sendHttpRequest<LinkedResource<Identifiable<Discussion>>>({
            endpoint: task._links.discussion.href,
            method: HttpMethods.GET,
            baseUrl: "",
          })
          .then((discussion) =>
            httpClient
              .sendHttpRequest<CommentsPage>({
                endpoint: `/comments/search/findByDiscussion_Uuid?discussionUuid=${discussion.uuid}&page=${page}&size=${size}&sort=createdDate,desc`,
                method: HttpMethods.GET,
              })
              .then((commentsPage) =>
                resolveCommentsPage(httpClient, linker, commentsPage),
              )
              .then((comments) => ({ ...discussion, comments })),
          ),
      );
}

function getEventDiscussion(httpClient: HttpClient, linker: Linker) {
  return (subject: string, page: number, size: number) =>
    httpClient
      .sendHttpRequest<LinkedResource<Event>>({
        endpoint: `/events/${subject}`,
        method: HttpMethods.GET,
      })
      .then((task) =>
        httpClient
          .sendHttpRequest<LinkedResource<Identifiable<Discussion>>>({
            endpoint: task._links.discussion.href,
            method: HttpMethods.GET,
            baseUrl: "",
          })
          .then((discussion) =>
            httpClient
              .sendHttpRequest<CommentsPage>({
                endpoint: `/comments/search/findByDiscussion_Uuid?discussionUuid=${discussion.uuid}&page=${page}&size=${size}&sort=createdDate,desc`,
                method: HttpMethods.GET,
              })
              .then((commentsPage) =>
                resolveCommentsPage(httpClient, linker, commentsPage),
              )
              .then((comments) => ({ ...discussion, comments })),
          ),
      );
}

function getListDiscussion(httpClient: HttpClient, linker: Linker) {
  return (subject: string, page: number, size: number) =>
    httpClient
      .sendHttpRequest<LinkedResource<List>>({
        endpoint: `/activities/${subject}`,
        method: HttpMethods.GET,
      })
      .then((task) =>
        httpClient
          .sendHttpRequest<LinkedResource<Identifiable<Discussion>>>({
            endpoint: task._links.discussion.href,
            method: HttpMethods.GET,
            baseUrl: "",
          })
          .then((discussion) =>
            httpClient
              .sendHttpRequest<CommentsPage>({
                endpoint: `/comments/search/findByDiscussion_Uuid?discussionUuid=${discussion.uuid}&page=${page}&size=${size}&sort=createdDate,desc`,
                method: HttpMethods.GET,
              })
              .then((commentsPage) =>
                resolveCommentsPage(httpClient, linker, commentsPage),
              )
              .then((comments) => ({ ...discussion, comments })),
          ),
      );
}

function getMilestoneDiscussion(httpClient: HttpClient, linker: Linker) {
  return (subject: string, page: number, size: number) =>
    httpClient
      .sendHttpRequest<LinkedResource<List>>({
        endpoint: `/milestones/${subject}`,
        method: HttpMethods.GET,
      })
      .then((task) =>
        httpClient
          .sendHttpRequest<LinkedResource<Identifiable<Discussion>>>({
            endpoint: task._links.discussion.href,
            method: HttpMethods.GET,
            baseUrl: "",
          })
          .then((discussion) =>
            httpClient
              .sendHttpRequest<CommentsPage>({
                endpoint: `/comments/search/findByDiscussion_Uuid?discussionUuid=${discussion.uuid}&page=${page}&size=${size}&sort=createdDate,desc`,
                method: HttpMethods.GET,
              })
              .then((commentsPage) =>
                resolveCommentsPage(httpClient, linker, commentsPage),
              )
              .then((comments) => ({ ...discussion, comments })),
          ),
      );
}

function getProjectDiscussion(httpClient: HttpClient, linker: Linker) {
  return (subject: string, page: number, size: number) =>
    httpClient
      .sendHttpRequest<LinkedResource<Project>>({
        endpoint: `/projects/${subject}`,
        method: HttpMethods.GET,
      })
      .then((task) =>
        httpClient
          .sendHttpRequest<LinkedResource<Identifiable<Discussion>>>({
            endpoint: task._links.discussion.href,
            method: HttpMethods.GET,
            baseUrl: "",
          })
          .then((discussion) =>
            httpClient
              .sendHttpRequest<CommentsPage>({
                endpoint: `/comments/search/findByDiscussion_Uuid?discussionUuid=${discussion.uuid}&page=${page}&size=${size}&sort=createdDate,desc`,
                method: HttpMethods.GET,
              })
              .then((commentsPage) =>
                resolveCommentsPage(httpClient, linker, commentsPage),
              )
              .then((comments) => ({ ...discussion, comments })),
          ),
      );
}

function getDiscussion(httpClient: HttpClient, linker: Linker) {
  return (subject: string, type: string, page: number, size: number) =>
    type === "task"
      ? getTaskDiscussion(httpClient, linker)(subject, page, size)
      : type === "event"
        ? getEventDiscussion(httpClient, linker)(subject, page, size)
        : type === "list"
          ? getListDiscussion(httpClient, linker)(subject, page, size)
          : type === "milestone"
            ? getMilestoneDiscussion(httpClient, linker)(subject, page, size)
            : type === "project"
              ? getProjectDiscussion(httpClient, linker)(subject, page, size)
              : Promise.reject();
}

export { getDiscussion };
